import {showMessage} from 'react-native-flash-message';
import {Linking} from 'react-native';

import getRemoteConfigValue from 'utils/firebase/remoteConfig';
import {setDynamicLink} from './actions';
import {setToken} from 'store/meeting/actions/properties';
import {setMe} from 'store/communication/actions/stateActions';
import {translate} from 'i18n/context/I18nContext';
import {VerifyAndParseToken} from 'utils/communication/Token';
import {logEvent} from 'utils/firebase/analytics';
import {truncateFirebaseAnalyticParameterValue} from 'utils/firebase/utils';
import {
  SET_USER_PERMISSIONS,
  INVALID_TOKEN,
} from 'utils/firebase/analytics.config';

// ─── Avatar URL persistence (web only) ──────────────────────────────────────
//
// After the customizer SPA finishes Save & Continue → Continue as Guest, it
// posts `{ type: 'ava-twin:avatar-saved', avatar_id, glb_url, skin_tone }` to
// `window.top`. We catch that here, write the avatar id + skin tone to the
// browser URL via `replaceState` (so a refresh skips the customizer), and
// dispatch `setAvatarData` so AvatarPreview can react and jump to
// DeviceConfig.

// Read avatar_id + skin_tone from URL on boot. Returns null if avatar_id
// missing — skin_tone is optional and may be null.
export const readAvatarParamsFromUrl = () => {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const avatarId = params.get('avatar_id');
  const skinTone = params.get('skin_tone');
  if (!avatarId) return null;
  return {avatarId, skinTone: skinTone || null};
};

// Persist avatar_id + skin_tone to the URL via replaceState (no navigation).
export const writeAvatarParamsToUrl = (avatarId, skinTone) => {
  if (typeof window === 'undefined') return;
  try {
    const url = new URL(window.location.href);
    if (avatarId) url.searchParams.set('avatar_id', avatarId);
    if (skinTone) url.searchParams.set('skin_tone', skinTone);
    window.history.replaceState({}, '', url.toString());
  } catch (e) {
    // Non-fatal — locked-down browsers / older user agents.
    console.warn('[marcus] writeAvatarParamsToUrl failed', e);
  }
};

// Install a postMessage listener that catches "ava-twin:avatar-saved" from
// the customizer iframe (which now posts to window.top thanks to the SDK
// customizer SPA fix), writes the params to the URL, and dispatches
// setAvatarData so the rest of the app can react. Also dispatches
// setCustomizerClosing(true) so a fullscreen <Loading> overlay covers the
// gap between the iframe closing and DeviceConfig rendering. Returns an
// unsubscribe function.
export const installAvatarSavedListener = (
  dispatch,
  setAvatarData,
  setCustomizerClosing,
) => {
  if (typeof window === 'undefined') return () => {};
  const onMessage = (event) => {
    const data = event.data;
    if (!data || typeof data !== 'object') return;
    if (data.type !== 'ava-twin:avatar-saved') return;
    const avatarId = data.avatar_id;
    const skinTone = data.skin_tone;
    if (!avatarId) return;
    console.log(
      '[WEBGL_DBG] linkActions: ava-twin:avatar-saved received avatarId=' +
        avatarId +
        ' skinTone=' +
        (skinTone || 'null'),
    );
    writeAvatarParamsToUrl(avatarId, skinTone);
    if (typeof setAvatarData === 'function') {
      dispatch(setAvatarData(avatarId, skinTone || null));
    }
    if (typeof setCustomizerClosing === 'function') {
      dispatch(setCustomizerClosing(true));
    }
  };
  window.addEventListener('message', onMessage);
  return () => window.removeEventListener('message', onMessage);
};

export const initDynamicLinkHandler = () => {
  const marcusSiteUrl = getRemoteConfigValue('marcus_site_url');
  const articlePath = '/blog/issues-joining-a-3dmeet-meeting/';
  return async (dispatch, getState) => {
    console.log('[WEBGL_DBG] linkActions: reading token from URL');
    const token = new URLSearchParams(window.location.search).get('token');
    console.log('[WEBGL_DBG] linkActions: token present=' + (token ? 'yes (len=' + token.length + ')' : 'no'));

    // Verify token (reject/return to previous state if invalid)
    const verifiedToken = VerifyAndParseToken(token);
    const {error} = verifiedToken;
    console.log('[WEBGL_DBG] linkActions: token verify result=' + (!error ? 'OK' : 'FAILED err=' + (error.message || error)));
    if (error) {
      await dispatch(setDynamicLink(undefined));

      if (token) {
        showMessage({
          type: 'danger',
          message: translate('linkActionsWeb.clickForSupport'),
          autoHide: false,
          onPress: () => {
            Linking.openURL(`${marcusSiteUrl.asString()}${articlePath}`);
          },
        });

        await logEvent(INVALID_TOKEN, {
          error: error,
        });

        console.error(error);
      }
    } else {
      await dispatch(setDynamicLink(token));
      await dispatch(setToken(token));

      const {peerId, permissions, roomId, integration} = verifiedToken;
      const displayName = translate('linkActionsWeb.userNum', {
        num: peerId.substring(0, 4),
      });
      const displayNameSet = false;
      const device = {};
      await dispatch(
        setMe({peerId, displayName, displayNameSet, device, permissions}),
      );

      const permissionsString = permissions.join(' ');
      await logEvent(SET_USER_PERMISSIONS, {
        meeting_id: roomId,
        integration: integration,
        permissions: truncateFirebaseAnalyticParameterValue(permissionsString),
      });
    }
  };
};
